import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import { adminAuthAPI } from '../services/adminApi';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import PasswordInput from '../components/PasswordInput';

const LoginContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #043663 0%, #032a4a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const LoginCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  padding: 3rem;
  width: 100%;
  max-width: 400px;
`;

const LogoSection = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  
  img {
    width: 80px;
    height: 80px;
    border-radius: 12px;
    margin-bottom: 1rem;
  }
  
  h1 {
    color: #1f2937;
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 0 0.5rem 0;
  }
  
  p {
    color: #6b7280;
    font-size: 0.9rem;
    margin: 0;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #043663;
  }
  
  &.error {
    border-color: #d52b1e;
  }
`;

const ErrorMessage = styled.span`
  color: #d52b1e;
  font-size: 0.75rem;
`;

const SubmitButton = styled.button`
  background: #043663;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  &:hover {
    background: #032a4a;
  }
  
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const AdminLoginPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAdminAuth();
  
  const {
    register,
    control,
    handleSubmit,
    formState: { errors }
  } = useForm();

  const onSubmit = async (data) => {
    setIsLoading(true);
    console.log('🔐 Attempting admin login with:', { email: data.email });
    
    try {
      const response = await adminAuthAPI.login(data);
      console.log('📡 Login response:', response);

      if (response && response.success && response.token) {
        // Use the context login function to update the state (including tenant info)
        login(response.user, response.token, response.tenant);
        toast.success('Login successful!');
        
        // Super Admin must always go to tenant list; tenant admin uses backend redirectUrl
        const isSuperAdmin = response.user?.isSuperAdmin === true;
        const redirectUrl = isSuperAdmin
          ? '/admin/tenants'
          : (response.redirectUrl || '/admin/dashboard');
        console.log('🔄 Admin redirecting to:', redirectUrl, isSuperAdmin ? '(Super Admin)' : '');
        console.log('🏢 Admin tenant:', response.tenant);
        navigate(redirectUrl);
      } else if (response && response.success && !response.token) {
        console.error('Admin login missing token in response');
        toast.error('Login response incomplete. Please try again or contact support.');
      } else {
        const errorMsg = response?.message || 'Login failed';
        console.error('❌ Login failed:', errorMsg);
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('💥 Login error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL
        }
      });
      
      let errorMessage = 'Network error. Please try again.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 401) {
        errorMessage = 'Invalid email or password';
      } else if (error.response?.status === 404) {
        errorMessage = 'Admin login endpoint not found';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogoClick = () => {
    if (location.pathname === '/') {
      // Already on home page, scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Navigate to home page and scroll to top
      navigate('/');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        <LogoSection>
          <img 
            src="/images/logo/logo.png" 
            alt="Helvetic Padel Series Logo" 
            onClick={handleLogoClick}
            style={{ cursor: 'pointer', transition: 'transform 0.2s ease' }}
            onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
          />
          <h1>Admin Portal</h1>
          <p>Helvetic Padel Series Management</p>
        </LogoSection>
        
        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address'
                }
              })}
              className={errors.email ? 'error' : ''}
              placeholder="admin@helveticpadelleague.ch"
            />
            {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="password">Password</Label>
            <Controller
              name="password"
              control={control}
              rules={{
                required: 'Password is required',
                minLength: {
                  value: 6,
                  message: 'Password must be at least 6 characters'
                }
              }}
              render={({ field }) => (
                <PasswordInput
                  id="password"
                  {...field}
                  className={errors.password ? 'error' : ''}
                  placeholder="Enter your password"
                  padding="0.75rem 1rem"
                  paddingRight="2.5rem"
                  border="2px solid #e5e7eb"
                  borderRadius="8px"
                  fontSize="1rem"
                  focusBorderColor="#043663"
                  errorBorderColor="#d52b1e"
                  autoComplete="current-password"
                />
              )}
            />
            {errors.password && <ErrorMessage>{errors.password.message}</ErrorMessage>}
          </FormGroup>

          <SubmitButton type="submit" disabled={isLoading}>
            {isLoading ? 'Signing In...' : 'Sign In'}
          </SubmitButton>
        </Form>
      </LoginCard>
    </LoginContainer>
  );
};

export default AdminLoginPage;
